import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import {
	Button,
	Editable,
	Empty,
	Label,
	LanguageSelect,
	Paragraph,
	Separator,
	Skeleton,
} from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import { LocalizationScope } from 'packages/kakapo-ui-application/contexts/LocalizationScope';
import useLocalizedState from 'packages/kakapo-ui-application/hooks/useLocalizedState';

const Explanation = styled(Flex)`
	background: ${(props) => props.theme.colors.whiteBlue};
`;

const Container = styled(Box)`
	height: 100%;
	background: ${(props) => props.theme.colors.whiteBlue};
`;

const Email = styled(Flex)`
	flex-direction: column;
`;

const Logo = styled(Box)`
	background-image: url('${(props) => props.theme.images.logo}?d=300x100/contain');
	background-size: contain;
	background-repeat: no-repeat;
	background-position: center;
	width: 200px;
	height: 50px;
	display: inline-block;
	vertical-align: top;
`;

const Contents = styled(Box)`
	background: white;
`;

const Footer = styled(Box)`
	font-size: 12px;
`;

const Area = styled(Flex)`
	border: 1px dashed ${(props) => props.theme.colors.lightBlue};
	border-radius: 5px;
`;

function CustomArea(props: {
	value: graphql.LocalizedAttributedString;
	onChange: (value: graphql.LocalizedAttributedString) => Promise<void>;
	empty: string;
}) {
	const [value, setValue] = useState(props.value);
	const [localizedValue, setLocalizedValue] = useLocalizedState(value, setValue);

	return (
		<Editable.Quill.Button
			value={localizedValue}
			onChange={async (value) => {
				await props.onChange(await setLocalizedValue(value));
			}}
		>
			{(value, { button }) => (
				<Area p={2}>
					<Box flexGrow={1} flexShrink={1}>
						{value ? (
							<Paragraph>{value}</Paragraph>
						) : (
							<Skeleton.Empty contents={<Empty small description={props.empty} />}>
								<Paragraph.Skeleton lines={3} width="100%" />
							</Skeleton.Empty>
						)}
					</Box>
					{button}
				</Area>
			)}
		</Editable.Quill.Button>
	);
}

function CustomPlainArea(props: {
	value: graphql.LocalizedString;
	onChange: (value: graphql.LocalizedString) => Promise<void>;
	empty: string;
}) {
	const [value, setValue] = useState(props.value);
	const [localizedValue, setLocalizedValue] = useLocalizedState(value, setValue);

	return (
		<Editable.Button
			value={localizedValue}
			onChange={async (value) => {
				await props.onChange(await setLocalizedValue(value));
			}}
		>
			{(value, { button }) => (
				<Area p={2}>
					<Box flexGrow={1} flexShrink={1}>
						{value ? (
							<Paragraph>{value}</Paragraph>
						) : (
							<Skeleton.Empty contents={<Empty small description={props.empty} />}>
								<Paragraph.Skeleton lines={3} width="100%" />
							</Skeleton.Empty>
						)}
					</Box>
					{button}
				</Area>
			)}
		</Editable.Button>
	);
}

function ContentsWithData(props: { settings: graphql.WorkspaceNotificationSettings }) {
	const { t } = useTranslation();

	const [update] = graphql.useUpdateWorkspaceNotificationSettingsMutation();
	const [updateWelcome] = graphql.useUpdateWelcomeEmailMutation();

	return (
		<Contents my={4} p={3} width="100%" maxWidth={600}>
			<Box mb={3}>
				<Label mb={1}>{t('settings.notifications.email.labels.header')}:</Label>
				<CustomArea
					empty={t('settings.notifications.email.placeholders.header')}
					value={props.settings.header}
					onChange={async (header) => {
						await update({
							variables: {
								input: {
									header,
									footer: props.settings.footer,
								},
							},
						});
					}}
				/>
			</Box>
			<Box mb={3}>
				<Label mb={1}>{t('settings.notifications.email.labels.subject')}:</Label>
				<CustomPlainArea
					empty={t('settings.notifications.email.placeholders.subject')}
					value={props.settings.welcomeEmail.subject}
					onChange={async (subject) => {
						await updateWelcome({
							variables: {
								input: {
									subject,
									body: props.settings.welcomeEmail.body,
								},
							},
						});
					}}
				/>
			</Box>
			<Box mb={3}>
				<Label mb={1}>{t('settings.notifications.email.labels.welcome')}:</Label>
				<CustomArea
					empty={t('settings.notifications.email.placeholders.welcome')}
					value={props.settings.welcomeEmail.body}
					onChange={async (body) => {
						await updateWelcome({
							variables: {
								input: {
									subject: props.settings.welcomeEmail.subject,
									body,
								},
							},
						});
					}}
				/>
			</Box>
			<Box>
				<Label mb={1}>{t('settings.notifications.email.labels.footer')}:</Label>
				<CustomArea
					empty={t('settings.notifications.email.placeholders.footer')}
					value={props.settings.footer}
					onChange={async (footer) => {
						await update({
							variables: {
								input: {
									header: props.settings.header,
									footer,
								},
							},
						});
					}}
				/>{' '}
			</Box>
		</Contents>
	);
}

function ContentsSkeleton() {
	const { t } = useTranslation();

	return (
		<Contents my={4} p={3} width="100%" maxWidth={600}>
			<Box mb={3}>
				<Label mb={1}>{t('settings.notifications.email.labels.header')}:</Label>
				<Area p={2}>
					<Paragraph.Skeleton lines={3} width="100%" />
				</Area>
			</Box>
			<Box mb={3}>
				<Label mb={1}>{t('settings.notifications.email.labels.subject')}:</Label>
				<Area p={2}>
					<Paragraph.Skeleton lines={1} width="100%" />
				</Area>
			</Box>
			<Box mb={3}>
				<Label mb={1}>{t('settings.notifications.email.labels.welcome')}:</Label>
				<Area p={2}>
					<Paragraph.Skeleton lines={3} width="100%" />
				</Area>
			</Box>
			<Box>
				<Label mb={1}>{t('settings.notifications.email.labels.footer')}:</Label>
				<Area p={2}>
					<Paragraph.Skeleton lines={3} width="100%" />
				</Area>
			</Box>
		</Contents>
	);
}

export default function NotificationsEmailEditor() {
	const { t } = useTranslation();

	const query = graphql.useWorkspaceNotificationSettingsQuery({
		fetchPolicy: 'network-only',
	});

	return (
		<LocalizationScope>
			<Explanation px={4} justifyContent="center">
				<Box>
					<Flex maxWidth={600} py={4}>
						<Paragraph>{t('settings.notifications.email.explanation')}</Paragraph>
						<Box ml={3} flexGrow={0} flexShrink={0}>
							<LanguageSelect.Scope />
						</Box>
					</Flex>
					<Separator />
				</Box>
			</Explanation>
			<Container pt={4} pb={6}>
				<Email alignItems="center">
					<Area p={2} alignItems="center">
						<Logo />
						<Button
							small
							type="transparent"
							title={t('settings.notifications.email.edit')}
							ml={3}
							link="/settings/organization/theme"
						/>
					</Area>
					{query.data ? (
						<ContentsWithData
							settings={
								query.data.viewer.workspace
									.notificationSettings as graphql.WorkspaceNotificationSettings
							}
						/>
					) : (
						<ContentsSkeleton />
					)}
					<Footer>{t('settings.notifications.email.address')}</Footer>
				</Email>
			</Container>
		</LocalizationScope>
	);
}
